/**
 * "How I appear on the report" (docs/ux/dr.md): the registry record's
 * display name, credential line, signature image and block, and the
 * preview the release prints, read only. The same read pieces the registry
 * page uses, without its editing contract; changes are made on the registry
 * record, which the link opens.
 */

import { ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router';
import { useOnline } from '@/app/online';
import { useIsPhone } from '@/app/viewport';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ReadGrid, ReadPair } from '@/features/registry/ConfigPanel';
import { SignaturePreview } from '@/features/registry/panels';
import { signatureUrl, useDoctor } from '@/features/registry/useRegistry';

export function ProfileTab({ slug, mine }: { slug: string; mine: boolean }) {
  const doctor = useDoctor(slug);
  const phone = useIsPhone();
  const online = useOnline();
  if (doctor.isPending) return <Skeleton className="h-64 rounded-xl" />;
  if (doctor.isError) return <ErrorPanel message="The registry record could not be loaded." onRetry={() => void doctor.refetch()} offline={!online} />;
  if (!doctor.data) {
    return <EmptyState title={`No registry record for ${slug}.`} description={mine ? 'Ask an administrator to add you on the registry; your reports still list on the other tab.' : 'The doctor has cases but no registry record.'} />;
  }
  const d = doctor.data.doctor;
  return (
    <section aria-labelledby="dr-profile-heading" data-slot="profile-panel" className="mx-auto max-w-[900px] rounded-xl border border-beam-line bg-beam-paper p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 id="dr-profile-heading" className="eyebrow text-beam-text-muted">On the report</h2>
        <Link to={`/registry/doctors/${encodeURIComponent(d.slug)}`} className={buttonVariants({ variant: 'outline', size: phone ? 'lg' : 'sm' }) + (phone ? ' h-11 w-full' : '')}>
          Open the registry record <ArrowRightIcon aria-hidden="true" className="size-4" />
        </Link>
      </div>
      <div className="flex flex-col gap-4">
        <ReadGrid columns={phone ? 1 : 2}>
          <ReadPair label="Display name" value={d.displayName} />
          <ReadPair label="Credential line" value={d.display.credentialLine} />
          <ReadPair label="Signature image" value={d.signature.assetKey ? <img src={signatureUrl(d.slug, d.signature.version)} alt={`Signature image for ${d.name}`} className="max-h-12" /> : <span className="text-beam-text-muted">No image</span>} />
          <ReadPair label="Signature block" value={d.signature.blockText ? <span className="whitespace-pre-line">{d.signature.blockText}</span> : null} />
        </ReadGrid>
        <SignaturePreview doctor={d} blockText={d.signature.blockText} displayName={d.displayName} credentialLine={d.display.credentialLine} />
        <p className="text-[13px] text-beam-text-muted">Changes to these fields are made on the registry record; the release reads them at build time.</p>
      </div>
    </section>
  );
}
